import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Badge,
  useMantineColorScheme,
  Center,
  Loader,
  Collapse,
  ActionIcon,
  Tooltip,
  Modal,
} from '@mantine/core';
import type { MantineColorScheme } from '@mantine/core';
import { IconPlayerPause, IconPlayerPlay, IconDeviceFloppy, IconX, IconInfoCircle, IconCheck , IconBook, IconTag } from '@tabler/icons-react';
import examenService from '../services/examenService';
import {
  IniciarExamenResponse,
  RespuestaUsuario,
  ExamenEnProgreso,
  RecuperarProgresoResponse
} from '../types/examen.types';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';
import RecuperarExamenModal from '../components/RecuperarExamenModal';
import { authService } from '../services/authService';
import { examenStorage } from '../utils/examenStorage';

// Reusable option card with stable identity to avoid remounts on timer updates
const OptionCard: React.FC<{
  value: string;
  label: React.ReactNode;
  selected: boolean;
  disabled?: boolean;
  onSelect: (value: string) => void;
  colorScheme: MantineColorScheme;
  palette: 'blue' | 'amber';
}> = ({ value, label, selected, disabled, onSelect, colorScheme, palette }) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const isInteractive = !disabled && !selected;
  
  // Reset hover state when selection changes
  useEffect(() => {
    if (selected || disabled) {
      setHovered(false);
      setFocused(false);
    }
  }, [selected, disabled]);

  const colors = palette === 'blue'
    ? {
        hoverBgDark: 'rgba(59, 130, 246, 0.10)',
        hoverBgLight: 'rgba(59, 130, 246, 0.06)',
        selectedBgDark: 'rgba(59, 130, 246, 0.12)',
        selectedBgLight: 'rgba(59, 130, 246, 0.10)',
        selectedBorderDark: '#93c5fd',
        selectedBorderLight: '#3b82f6',
        hoverBorder: '#60a5fa',
        hoverShadowDark: '0 10px 24px rgba(0,0,0,0.35)',
        hoverShadowLight: '0 8px 20px rgba(59,130,246,0.15)'
      }
    : {
        hoverBgDark: 'rgba(245, 158, 11, 0.12)',
        hoverBgLight: 'rgba(245, 158, 11, 0.06)',
        selectedBgDark: 'rgba(245, 158, 11, 0.14)',
        selectedBgLight: 'rgba(245, 158, 11, 0.10)',
        selectedBorderDark: '#fbbf24',
        selectedBorderLight: '#f59e0b',
        hoverBorder: '#fbbf24',
        hoverShadowDark: '0 10px 24px rgba(0,0,0,0.35)',
        hoverShadowLight: '0 8px 20px rgba(245, 158, 11, 0.20)'
      };

  const bgColor = selected
    ? (colorScheme === 'dark' ? colors.selectedBgDark : colors.selectedBgLight)
    : (hovered || focused)
      ? (colorScheme === 'dark' ? colors.hoverBgDark : colors.hoverBgLight)
      : (colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#ffffff');

  const borderClr = selected
    ? (colorScheme === 'dark' ? colors.selectedBorderDark : colors.selectedBorderLight)
    : (hovered || focused)
      ? colors.hoverBorder
      : undefined;

  const boxSh = selected
    ? 'none'
    : (hovered || focused)
      ? (colorScheme === 'dark' ? colors.hoverShadowDark : colors.hoverShadowLight)
      : '0 0 0 rgba(0,0,0,0)';

  const transform = selected ? 'none' : (hovered ? 'translateY(-2px)' : 'none');

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect(value)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect(value);
        }
      }}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderColor: borderClr,
        borderWidth: selected ? 2 : 1,
        boxShadow: boxSh,
        backgroundColor: bgColor,
        transition: 'background-color 150ms ease, border-color 150ms ease, border-width 150ms ease, box-shadow 150ms ease, transform 150ms ease',
        userSelect: 'none',
        transform,
      }}
      onMouseEnter={() => isInteractive && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => isInteractive && setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <Group align="flex-start" gap="md" wrap="nowrap">
        <Badge
          size="md"
          variant={selected ? 'filled' : 'light'}
          color={selected ? 'dark' : 'gray'}
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 600,
            fontSize: '14px',
          }}
        >
          {value.toUpperCase()}
        </Badge>
        <Text style={{ lineHeight: 1.5, flex: 1, textAlign: 'left' }}>{label}</Text>
      </Group>
    </Paper>
  );
};

const ExamenRapidoPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colorScheme } = useMantineColorScheme();
  const currentUser = authService.getCurrentUserFromStorage();
  const yaVerificado = React.useRef(false);

  // Estado del examen
  const [examenData, setExamenData] = useState<IniciarExamenResponse | null>(null);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState<Map<number, string | null>>(new Map());
  const [tiempoInicio, setTiempoInicio] = useState<Date | null>(null);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [loading, setLoading] = useState(false);
  const [iniciandoExamen, setIniciandoExamen] = useState(false);
  const [pausado, setPausado] = useState(false);
  const [modalFinalizarAbierto, setModalFinalizarAbierto] = useState(false);
  const [mensajeCarga, setMensajeCarga] = useState(0);
  const [finalizando, setFinalizando] = useState(false); // Estado para detener auto-guardado
  // Selección temporal (no guardada) de la pregunta actual
  const [seleccionTemporal, setSeleccionTemporal] = useState<string | null>(null);

  // Estado para configuración inicial. Arranca en false y solo se activa una vez que
  // verificamos que NO hay un examen en progreso; así evitamos el parpadeo del modal
  // de dificultad antes de mostrar el de continuar/nuevo.
  const [verificando, setVerificando] = useState(true);
  const [mostrarModalConfiguracion, setMostrarModalConfiguracion] = useState(false);
  const [dificultadSeleccionada, setDificultadSeleccionada] = useState<string | null>(null);

  // Estado para recuperación de examen
  const [examenEnProgreso, setExamenEnProgreso] = useState<ExamenEnProgreso | null>(null);
  const [mostrarModalRecuperar, setMostrarModalRecuperar] = useState(false);
  const [cargandoRecuperacion, setCargandoRecuperacion] = useState(false);

  // Modal para avisar que hay otro examen en progreso (de otro tipo)
  const [mostrarModalOtroExamen, setMostrarModalOtroExamen] = useState(false);
  const [otroExamen, setOtroExamen] = useState<ExamenEnProgreso | null>(null);

  // Modal de confirmación al pausar y guardar
  const [mostrarModalGuardado, setMostrarModalGuardado] = useState(false);

  // Rotar mensajes de carga
  useEffect(() => {
    if (!iniciandoExamen) return;
    
    const interval = setInterval(() => {
      setMensajeCarga((prev) => (prev + 1) % 4);
    }, 1500); // Cambiar mensaje cada 1.5 segundos
    
    return () => clearInterval(interval);
  }, [iniciandoExamen]);

  // Verificar examen en progreso al cargar
  useEffect(() => {
    verificarExamenEnProgreso();
  }, []);

  const verificarExamenEnProgreso = async () => {
    // Evitar doble ejecución (React StrictMode ejecuta useEffect 2 veces)
    if (yaVerificado.current) {
      console.log('⚠️ Ya se verificó, evitando duplicación');
      return;
    }
    yaVerificado.current = true;

    if (!currentUser) {
      alert('No se pudo obtener el usuario actual');
      navigate('/');
      return;
    }

    // Si viene desde el simulador con "Continuar"
    if (location.state?.continuandoExamen && location.state?.examenEnProgreso) {
      console.log('✅ Continuando examen desde simulador');
      const examenParaContinuar = location.state.examenEnProgreso;
      setExamenEnProgreso(examenParaContinuar);
      setMostrarModalConfiguracion(false); // No volver a pedir la dificultad al continuar
      setIniciandoExamen(false);
      if (location.state?.continuarDirecto) {
        // El usuario ya eligió "Continuar" en el simulador: entrar directo, sin modal
        await recuperarExamen(examenParaContinuar);
      } else {
        setMostrarModalRecuperar(true);
      }
      setVerificando(false);
      return;
    }

    try {
      // Verificar solo exámenes RAPIDO en progreso (permitiendo otros tipos simultáneamente)
      const examen = await examenService.verificarExamenEnProgresoPorTipo(currentUser.id, 'RAPIDO');
      if (examen) {
        setExamenEnProgreso(examen);
        setMostrarModalRecuperar(true);
        setMostrarModalConfiguracion(false);
      } else {
        // No hay examen RAPIDO en progreso, mostrar modal de configuración
        setMostrarModalConfiguracion(true);
      }
    } catch (error) {
      console.error('Error verificando examen en progreso:', error);
      // En caso de error, mostrar configuración para empezar de cero
      setMostrarModalConfiguracion(true);
    } finally {
      setVerificando(false);
    }
  };

  // Sincronizar selección temporal cuando cambia la pregunta
  useEffect(() => {
    if (!examenData) return;
    const p = examenData.preguntas[preguntaActual];
    if (!p) return;
    // Sincronizar solo cuando cambia la pregunta, no cuando cambian las respuestas
    setSeleccionTemporal(prev => {
      const guardada = respuestas.get(p.orden);
      return guardada ?? null;
    });
  }, [examenData, preguntaActual]);

  // Timer para el examen
  useEffect(() => {
    if (!tiempoInicio || pausado) return;

    const interval = setInterval(() => {
      const ahora = new Date();
      const diferencia = Math.floor((ahora.getTime() - tiempoInicio.getTime()) / 1000);
      setTiempoTranscurrido(diferencia);
    }, 1000);

    return () => clearInterval(interval);
  }, [tiempoInicio, pausado]);

  // Auto-guardar progreso cada 30 segundos (detener si está finalizando)
  useEffect(() => {
    if (!examenData || !tiempoInicio || finalizando) return;

    const interval = setInterval(() => {
      guardarProgresoActual();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [examenData, preguntaActual, tiempoTranscurrido, pausado, respuestas, finalizando]);

  const togglePausa = () => {
    if (pausado) {
      // Reanudar: ajustar tiempoInicio para compensar el tiempo pausado
      const tiempoPausado = tiempoTranscurrido;
      setTiempoInicio(new Date(Date.now() - tiempoPausado * 1000));
    } else {
      // Pausar: guardar el tiempo actual
      setTiempoTranscurrido(tiempoTranscurrido);
      guardarProgresoActual(); // Guardar inmediatamente al pausar
    }
    setPausado(!pausado);
  };

  const iniciarExamen = async () => {
    if (!currentUser) {
      alert('No se pudo obtener el usuario actual');
      navigate('/');
      return;
    }

    setIniciandoExamen(true);
    setMostrarModalConfiguracion(false);
    try {
      console.log('🚀 Iniciando examen rápido con dificultad:', dificultadSeleccionada);
      const dificultadId = dificultadSeleccionada ? parseInt(dificultadSeleccionada) : undefined;
      const data = await examenService.crearExamenRapido(currentUser.id, dificultadId);
      setExamenData(data);
      setTiempoInicio(new Date());
      console.log('✅ Examen iniciado:', data);
    } catch (error) {
      console.error('❌ Error al iniciar examen:', error);
      alert('Error al iniciar el examen. Intenta de nuevo.');
      navigate('/');
    } finally {
      setIniciandoExamen(false);
    }
  };

  const recuperarExamen = async (examenParam?: ExamenEnProgreso) => {
    const examen = examenParam ?? examenEnProgreso;
    if (!examen) return;

    // Verificar si el examen está 100% completado
    const estaCompletado = (examen.preguntaActual + 1) >= examen.totalPreguntas;
    if (estaCompletado) {
      console.log('🎯 Examen 100% completado, finalizando automáticamente...');
      setCargandoRecuperacion(true);
      try {
        const progreso = await examenService.recuperarProgreso(examen.intentoId);
        
        // Restaurar respuestas para finalizar
        const respuestasMap = new Map<number, string | null>();
        Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
          respuestasMap.set(parseInt(orden), respuesta);
        });
        
        // Crear el array de respuestas para finalizar
        const respuestasArray: RespuestaUsuario[] = progreso.examenData.preguntas.map((preg) => ({
          orden: preg.orden,
          respuesta: respuestasMap.get(preg.orden) || null,
          tiempoSeg: Math.floor(progreso.tiempoTranscurrido / progreso.examenData.totalPreguntas)
        }));

        const resultado = await examenService.finalizarExamen({
          intentoId: examen.intentoId,
          duracionSeg: progreso.tiempoTranscurrido,
          respuestas: respuestasArray
        });

        // Limpiar progreso guardado
        examenStorage.limpiarProgreso();

        console.log('✅ Examen finalizado, redirigiendo a resultados');
        navigate(`/estudiante/resultados/${examen.intentoId}`, { state: { resultado } });
      } catch (error) {
        console.error('❌ Error al finalizar examen completado:', error);
        alert('Error al finalizar el examen.');
      } finally {
        setCargandoRecuperacion(false);
      }
      return;
    }

    setCargandoRecuperacion(true);
    try {
      const progreso = await examenService.recuperarProgreso(examen.intentoId);

      // Restaurar estado completo
      setExamenData(progreso.examenData);
      setPreguntaActual(progreso.preguntaActual);
      setTiempoTranscurrido(progreso.tiempoTranscurrido);
      setPausado(progreso.pausado || false);

      // Restaurar respuestas
      const respuestasMap = new Map<number, string | null>();
      Object.entries(progreso.respuestas).forEach(([orden, respuesta]) => {
        respuestasMap.set(parseInt(orden), respuesta);
      });
      setRespuestas(respuestasMap);

      // Calcular tiempo de inicio ajustado
      const tiempoAjustado = new Date(Date.now() - progreso.tiempoTranscurrido * 1000);
      setTiempoInicio(tiempoAjustado);

      setMostrarModalRecuperar(false);

      console.log('✅ Examen recuperado exitosamente');
    } catch (error) {
      console.error('Error recuperando examen:', error);
      alert('Error al recuperar el examen');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const comenzarNuevoExamen = async () => {
    if (!examenEnProgreso) return;

    setCargandoRecuperacion(true);
    try {
      // Abandonar el examen anterior
      await examenService.abandonarExamen(examenEnProgreso.intentoId);
      examenStorage.limpiarProgreso();

      setMostrarModalRecuperar(false);
      // Iniciar nuevo examen
      await iniciarExamen();
    } catch (error) {
      console.error('Error abandonando examen:', error);
      alert('Error al abandonar el examen anterior');
    } finally {
      setCargandoRecuperacion(false);
    }
  };

  const guardarProgresoActual = async (respuestasOverride?: Map<number, string | null>) => {
    if (!examenData) return;

    try {
      // Convertir Map a objeto
      const respuestasFuente = respuestasOverride ?? respuestas;
      const respuestasObj: Record<number, string> = {};
      respuestasFuente.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor as string;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado,
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      console.log('💾 Progreso guardado');
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  };

  const pausarYGuardar = async () => {
    if (!examenData) return;

    setLoading(true);
    try {
      // Convertir Map a objeto
      const respuestasObj: Record<number, string> = {};
      respuestas.forEach((valor, clave) => {
        if (valor) respuestasObj[clave] = valor as string;
      });

      const request = {
        intentoId: examenData.intentoId,
        preguntaActual,
        tiempoTranscurrido,
        pausado: true, // Marcar como pausado
        respuestas: respuestasObj
      };

      // Guardar en backend
      await examenService.guardarProgreso(request);

      // Guardar en localStorage como backup
      examenStorage.guardarProgreso(request);

      // Actualizar estado local
      setPausado(true);

      console.log('💾 Progreso guardado y examen pausado');

      // Mostrar modal personalizado
      setMostrarModalGuardado(true);
    } catch (error) {
      console.error('Error al pausar y guardar:', error);
      alert('Error al guardar el progreso del examen.');
    } finally {
      setLoading(false);
    }
  };

  const seleccionarRespuesta = (respuesta: string) => {
    // Solo actualizar la selección temporal; NO guardar aún
    setSeleccionTemporal(respuesta);
  };

  const siguientePregunta = () => {
    if (!examenData) return;

    if (preguntaActual < examenData.preguntas.length - 1) {
      const nextIndex = preguntaActual + 1;
      const nextP = examenData.preguntas[nextIndex];
      setSeleccionTemporal(respuestas.get(nextP.orden) ?? null);
      setPreguntaActual(nextIndex);
    }
  };

  const preguntaAnterior = () => {
    if (preguntaActual > 0) {
      const prevIndex = preguntaActual - 1;
      const prevP = examenData!.preguntas[prevIndex];
      setSeleccionTemporal(respuestas.get(prevP.orden) ?? null);
      setPreguntaActual(prevIndex);
    }
  };

  const confirmarFinalizarExamen = async () => {
    if (!examenData || !tiempoInicio) return;

    setFinalizando(true); // Detener auto-guardado
    setModalFinalizarAbierto(false);
    setLoading(true);
    try {
      const respuestasArray: RespuestaUsuario[] = examenData.preguntas.map((pregunta) => ({
        orden: pregunta.orden,
        respuesta: respuestas.get(pregunta.orden) || null,
        tiempoSeg: Math.floor(tiempoTranscurrido / examenData.totalPreguntas)
      }));

      const resultado = await examenService.finalizarExamen({
        intentoId: examenData.intentoId,
        duracionSeg: tiempoTranscurrido,
        respuestas: respuestasArray
      });

      // Limpiar progreso guardado
      examenStorage.limpiarProgreso();

      navigate(`/estudiante/resultados/${examenData.intentoId}`, { state: { resultado } });
    } catch (error) {
      console.error('❌ Error al finalizar examen:', error);
      alert('Error al finalizar el examen.');
    } finally {
      setLoading(false);
    }
  };

  const formatearTiempo = (segundos: number): string => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (iniciandoExamen) {
    const mensajes = [
      { titulo: 'Generando Quiz Express', subtitulo: 'Seleccionando 10 preguntas aleatorias...' },
      { titulo: 'Mezclando especialidades', subtitulo: 'Para una experiencia completa...' },
      { titulo: 'Ajustando dificultad', subtitulo: 'Perfecta para práctica rápida...' },
      { titulo: 'Casi listo', subtitulo: 'Preparando tu examen...' }
    ];

    const mensajeActual = mensajes[mensajeCarga];

    return (
      <Box
        style={{
          minHeight: '100vh',
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack align="center" gap="xl" style={{ maxWidth: '450px', padding: '24px', width: '100%' }}>
          {/* Loader */}
          <Loader size={80} color={colorScheme === 'dark' ? 'gray' : 'dark'} />

          {/* Título con animación */}
          <Stack align="center" gap="sm" style={{ textAlign: 'center', width: '100%' }}>
            <Title
              order={3}
              style={{
                animation: 'fadeIn 0.5s ease-in-out',
                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
              }}
            >
              {mensajeActual.titulo}
            </Title>
            <Text
              size="sm"
              c="dimmed"
              style={{
                animation: 'fadeIn 0.5s ease-in-out 0.2s both',
              }}
            >
              {mensajeActual.subtitulo}
            </Text>
          </Stack>

          {/* Barra de progreso con ancho fijo */}
          <Box style={{ width: '100%', maxWidth: '400px' }}>
            <Box
              style={{
                height: '3px',
                background: colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                style={{
                  height: '100%',
                  width: `${((mensajeCarga + 1) / 4) * 100}%`,
                  background: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                  transition: 'width 0.5s ease',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                }}
              />
            </Box>
            <Text size="xs" c="dimmed" mt="xs" ta="center">
              {Math.round(((mensajeCarga + 1) / 4) * 100)}%
            </Text>
          </Box>

          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </Stack>
      </Box>
    );
  }

  // Mientras verificamos si hay un examen en progreso no mostramos ningún modal,
  // solo un loader. Evita el parpadeo del modal de dificultad antes de decidir.
  if (verificando) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Loader size="lg" color={colorScheme === 'dark' ? 'gray' : 'dark'} />
      </Center>
    );
  }

  // Si hay modales activos, mostrarlos aunque no haya examenData
  if (!examenData && !mostrarModalRecuperar && !mostrarModalOtroExamen && !mostrarModalConfiguracion) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Stack align="center">
          <Text size="xl">Error al cargar el examen</Text>
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
        </Stack>
      </Center>
    );
  }

  const pregunta = examenData?.preguntas[preguntaActual];
  const respuestaSeleccionada = seleccionTemporal;


  return (
    <>
      {/* Modal de configuración inicial */}
      <Modal
        opened={mostrarModalConfiguracion}
        onClose={() => {}}
        centered
        size="md"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{ content: { borderRadius: '16px' } }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={3} mb={4} style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Configurar Examen Rápido
            </Title>
            <Text size="sm" c="dimmed">
              Elige el nivel de dificultad de las preguntas
            </Text>
          </Box>

          <Stack gap="xs">
            {[
              { value: null, titulo: 'Mixta', desc: 'Combinación de los tres niveles', color: '' },
              { value: '1', titulo: 'Fácil', desc: 'Conceptos básicos', color: '#22c55e' },
              { value: '2', titulo: 'Intermedio', desc: 'Aplicación clínica', color: '#f59e0b' },
              { value: '3', titulo: 'Difícil', desc: 'Casos complejos', color: '#ef4444' },
            ].map((op) => {
              const seleccionada = dificultadSeleccionada === op.value;
              const esMix = op.value === null;
              const accent = esMix ? (colorScheme === 'dark' ? '#e2e8f0' : '#1e293b') : op.color;
              return (
                <Box
                  key={op.titulo}
                  onClick={() => setDificultadSeleccionada(op.value)}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: `1.5px solid ${seleccionada
                      ? accent
                      : (colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')}`,
                    backgroundColor: seleccionada
                      ? (esMix
                          ? (colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(30,41,59,0.04)')
                          : `${op.color}14`)
                      : 'transparent',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                  onMouseEnter={(e) => {
                    if (!seleccionada) {
                      e.currentTarget.style.backgroundColor = colorScheme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!seleccionada) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {/* Punto de color por nivel (mixta = combinación de los tres) */}
                  <Box
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: esMix
                        ? 'linear-gradient(135deg, #22c55e 0%, #f59e0b 50%, #ef4444 100%)'
                        : op.color,
                    }}
                  />
                  <Box style={{ flex: 1 }}>
                    <Text size="sm" fw={600} style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b' }}>
                      {op.titulo}
                    </Text>
                    <Text size="xs" c="dimmed">{op.desc}</Text>
                  </Box>
                  {seleccionada && (
                    <IconCheck size={18} style={{ color: accent, flexShrink: 0 }} />
                  )}
                </Box>
              );
            })}
          </Stack>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => {
                setMostrarModalConfiguracion(false);
                navigate('/estudiante/simulador');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="filled"
              color="dark"
              onClick={iniciarExamen}
              loading={iniciandoExamen}
            >
              Iniciar Examen
            </Button>
          </Group>
        </Stack>
      </Modal>

      <RecuperarExamenModal
        examen={examenEnProgreso}
        opened={mostrarModalRecuperar}
        onContinuar={() => recuperarExamen()}
        onNuevo={comenzarNuevoExamen}
        loading={cargandoRecuperacion}
      />

      {/* Modal: otro examen en progreso (estilo unificado) */}
      <Modal
        opened={mostrarModalOtroExamen}
        onClose={() => {
          // No permitir cerrar sin elegir una opción
        }}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{ content: { borderRadius: '16px' } }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Tienes un examen en progreso</Title>
            <Text size="sm" c="dimmed">
              {otroExamen?.nombreExamen}. Debes elegir continuar ese examen o abandonarlo para iniciar este.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button 
              variant="default" 
              radius="md" 
              loading={cargandoRecuperacion}
              onClick={async () => {
                if (!otroExamen) return;
                setCargandoRecuperacion(true);
                try {
                  // Abandonar el otro examen primero
                  await examenService.abandonarExamen(otroExamen.intentoId);
                  examenStorage.limpiarProgreso();
                  setMostrarModalOtroExamen(false);
                  setOtroExamen(null);
                  // Iniciar nuevo examen
                  await iniciarExamen();
                } catch (error) {
                  console.error('Error abandonando examen:', error);
                  alert('Error al abandonar el examen anterior');
                  setCargandoRecuperacion(false);
                }
              }}
            >
              Abandonar y empezar este
            </Button>
            <Button
              variant="filled"
              color="dark"
              radius="md"
              onClick={() => {
                if (!otroExamen) return;
                if (otroExamen.tipoExamen === 'ENARM') navigate('/estudiante/simulador/completo');
                else if (otroExamen.tipoExamen === 'FILTRADO') navigate(`/estudiante/examen-filtrado/${otroExamen.intentoId}`);
              }}
            >
              Ir a ese examen
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación para finalizar */}
      <Modal
        opened={modalFinalizarAbierto}
        onClose={() => setModalFinalizarAbierto(false)}
        centered
        size="md"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: {
            borderRadius: '16px',
            padding: '28px',
          },
        }}
      >
        <Stack gap="xl">
          {/* Header */}
          <Box>
            <Title order={3} mb="xs" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              {respuestas.size === examenData?.totalPreguntas ? 'Finalizar Examen' : 'Resumen del Examen'}
            </Title>
            <Text size="sm" c="dimmed">
              {respuestas.size === examenData?.totalPreguntas 
                ? 'Has completado todas las preguntas. ¿Deseas enviar el examen?' 
                : 'Revisa tu progreso antes de finalizar'}
            </Text>
          </Box>

          {/* Progreso */}
          <Box>
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="sm" fw={600}>Progreso del examen</Text>
                <Group gap="sm">
                  <Badge 
                    size="lg" 
                    variant="filled"
                    color={respuestas.size === examenData?.totalPreguntas ? 'green' : 'orange'}
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    {respuestas.size} / {examenData?.totalPreguntas}
                  </Badge>
                  {examenData && respuestas.size < examenData.totalPreguntas && (
                    <Tooltip
                      label="Haz clic para ir a la pregunta, o finalizar ahora sin contestar."
                      multiline
                      w={280}
                      withArrow
                      position="bottom-end"
                    >
                      <Box style={{ cursor: 'help', display: 'flex', alignItems: 'center' }}>
                        <IconInfoCircle size={18} style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }} />
                      </Box>
                    </Tooltip>
                  )}
                </Group>
              </Group>
              
              {examenData && respuestas.size < examenData.totalPreguntas && (
                <>
                  <Text size="sm" c="orange" fw={500}>
                    Tienes {examenData.totalPreguntas - respuestas.size} {(examenData.totalPreguntas - respuestas.size) === 1 ? 'pregunta' : 'preguntas'} sin responder
                  </Text>
                  <Box
                    style={{
                      maxHeight: '140px',
                      overflowY: 'auto',
                      padding: '12px',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                    }}
                  >
                    <Text size="xs" c="dimmed" mb="sm" fw={600}>Preguntas sin responder:</Text>
                    <Group gap="xs">
                      {examenData.preguntas.map((p, idx) => {
                        if (!respuestas.has(p.orden)) {
                          return (
                            <Badge
                              key={idx}
                              size="md"
                              variant="light"
                              color="gray"
                              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                              onClick={() => {
                                setModalFinalizarAbierto(false);
                                setPreguntaActual(idx);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              Pregunta {idx + 1}
                            </Badge>
                          );
                        }
                        return null;
                      })}
                    </Group>
                  </Box>
                </>
              )}
              
              {respuestas.size === examenData?.totalPreguntas && (
                <Text size="sm" c="green" fw={500}>
                  Has contestado todas las preguntas
                </Text>
              )}
            </Stack>
          </Box>

          {/* Botones */}
          <Group justify="flex-end" gap="sm" mt="md">
            <Button
              variant="default"
              onClick={() => setModalFinalizarAbierto(false)}
              radius="md"
              size="sm"
            >
              Regresar
            </Button>
            <Button
              variant="filled"
              color={examenData && respuestas.size < examenData.totalPreguntas ? 'red' : 'dark'}
              onClick={confirmarFinalizarExamen}
              loading={loading}
              radius="md"
              size="sm"
            >
              {examenData && respuestas.size < examenData.totalPreguntas ? 'Sí, finalizar de todos modos' : 'Finalizar Examen'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal de confirmación al pausar/guardar */}
      <Modal
        opened={mostrarModalGuardado}
        onClose={() => setMostrarModalGuardado(false)}
        centered
        size="sm"
        radius="lg"
        withCloseButton={false}
        overlayProps={{ blur: 3 }}
        styles={{
          content: { borderRadius: '16px' },
        }}
      >
        <Stack gap="lg">
          <Box>
            <Title order={4} mb="xs">Examen pausado</Title>
            <Text size="sm" c="dimmed">
              Examen pausado y guardado. Puedes continuar desde donde lo dejaste.
            </Text>
          </Box>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" radius="md" onClick={() => setMostrarModalGuardado(false)}>
              Cancelar
            </Button>
            <Button variant="filled" color="dark" radius="md" onClick={() => navigate('/estudiante/simulador')}>
              Ir al inicio
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Solo renderizar el contenido del examen si existe examenData */}
      {examenData && pregunta && (
      <Box
        style={{
          minHeight: '100vh',
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          padding: '24px',
        }}
      >
        <Paper
          shadow="md"
          radius="md"
          p="xl"
          withBorder
          style={{
            backgroundColor:
              colorScheme === 'dark' ? '#1e293b' : 'rgba(255,255,255,0.95)',
            maxWidth: '900px',
            margin: '0 auto'
          }}
        >
          {/* Header */}
          <Group justify="space-between" align="center" mb="xl">
            <Box style={{ textAlign: 'left' }}>
              <Title order={2} mb={4}>Examen Rápido</Title>
              <Text size="sm" c="dimmed">{examenData.totalPreguntas} preguntas</Text>
            </Box>
            
            {/* Mensaje de pausa (centrado) */}
            {pausado && (
              <Text size="10px" c="red" fw={500} style={{ flex: 1, textAlign: 'center' }}>
                Pausado - Reanuda para continuar
              </Text>
            )}
            
            {/* Timer, pausa y guardar */}
            <Group gap="sm">
              <Badge 
                color={colorScheme === 'dark' ? 'gray' : 'dark'} 
                variant="filled" 
                size="xl"
                style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '10px 16px',
                }}
              >
                {formatearTiempo(tiempoTranscurrido)}
              </Badge>
              <Tooltip label={pausado ? "Reanudar" : "Pausar"}>
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={togglePausa}
                >
                  {pausado ? <IconPlayerPlay size={20} /> : <IconPlayerPause size={20} />}
                </ActionIcon>
              </Tooltip>
              
              {/* Guardar y salir (solo icono) */}
              <Tooltip label="Guardar y salir">
                <ActionIcon
                  size="xl"
                  variant="filled"
                  color={colorScheme === 'dark' ? 'gray' : 'dark'}
                  onClick={pausarYGuardar}
                  disabled={loading}
                >
                  <IconDeviceFloppy size={20} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {/* Caso de estudio (si aplica) */}
          {pregunta.tipo === 'CASO' && pregunta.casoEstudioContenido && (
            <Paper
              p="md"
              mb="lg"
              withBorder
              style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#fafafa',
                borderLeft: `3px solid ${colorScheme === 'dark' ? 'rgba(148,163,184,0.4)' : '#e5e7eb'}`,
              }}
            >
              <Text fw={500} size="sm" mb="xs" c="dimmed">
                CASO CLÍNICO
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {pregunta.casoEstudioContenido}
              </Text>
            </Paper>
          )}

          {/* Pregunta */}
          <Box mb="sm">
            <Group justify="space-between" align="flex-start" mb="xs">
              <Title order={4} style={{ flex: 1, textAlign: 'left' }}>
                {preguntaActual + 1}. {pregunta.pregunta}
              </Title>

              {currentUser && (
                <QuejaSugerenciaButton
                  key={`queja-${pregunta.tipo}-${pregunta.tipo === 'CASO' ? pregunta.preguntaCasoId : pregunta.reactivoId}`}
                  reactivoId={pregunta.tipo === 'REACTIVO' ? pregunta.reactivoId : undefined}
                  preguntaCasoId={pregunta.tipo === 'CASO' ? pregunta.preguntaCasoId : undefined}
                  tipoObjetivo={pregunta.tipo === 'CASO' ? 'PREGUNTA_CASO' : 'REACTIVO'}
                />
              )}
            </Group>

            {/* Especialidad y Tema Badges */}
            <Group gap="xs" style={{ marginTop: '8px' }}>
              {pregunta.especialidadNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="blue"
                  leftSection={<IconBook size={13} />}
                >
                  {pregunta.especialidadNombre}
                </Badge>
              )}
              {pregunta.temaNombre && (
                <Badge
                  size="md"
                  variant="light"
                  color="cyan"
                  leftSection={<IconTag size={13} />}
                >
                  {pregunta.temaNombre}
                </Badge>
              )}
            </Group>
          </Box>

          {/* Opciones - estilo tarjetas */}
          <Stack gap="sm" mb="xl" style={{ opacity: pausado ? 0.5 : 1, pointerEvents: pausado ? 'none' : 'auto' }}>
            <OptionCard
              key={`opt-a-${preguntaActual}`}
              value="a"
              label={pregunta.respuestaA}
              selected={respuestaSeleccionada === 'a'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="blue"
            />
            <OptionCard
              key={`opt-b-${preguntaActual}`}
              value="b"
              label={pregunta.respuestaB}
              selected={respuestaSeleccionada === 'b'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="blue"
            />
            <OptionCard
              key={`opt-c-${preguntaActual}`}
              value="c"
              label={pregunta.respuestaC}
              selected={respuestaSeleccionada === 'c'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="blue"
            />
            <OptionCard
              key={`opt-d-${preguntaActual}`}
              value="d"
              label={pregunta.respuestaD}
              selected={respuestaSeleccionada === 'd'}
              disabled={pausado}
              onSelect={seleccionarRespuesta}
              colorScheme={colorScheme}
              palette="blue"
            />
          </Stack>

          {/* Footer con navegación y progreso */}
          <Box mt="xl" pt="xl" style={{ borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
            {/* Indicadores de progreso (dots) - Con indicador de respuestas */}
            <Group justify="center" gap={8} mb="lg">
              {examenData.preguntas.map((p, index) => {
                const tieneRespuesta = respuestas.has(p.orden);
                return (
                  <Tooltip key={index} label={`Pregunta ${index + 1}`}>
                    <Box
                      style={{
                        width: index === preguntaActual ? 32 : 10,
                        height: 10,
                        borderRadius: 5,
                        backgroundColor:
                          index === preguntaActual
                            ? colorScheme === 'dark' ? '#3b82f6' : '#2563eb'
                            : tieneRespuesta
                            ? colorScheme === 'dark' ? '#10b981' : '#059669'
                            : colorScheme === 'dark' ? '#475569' : '#cbd5e1',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        border: index === preguntaActual ? `2px solid ${colorScheme === 'dark' ? '#60a5fa' : '#1e40af'}` : 'none',
                        opacity: tieneRespuesta ? 1 : 0.5,
                      }}
                      onClick={() => {
                        const targetP = examenData.preguntas[index];
                        setSeleccionTemporal(respuestas.get(targetP.orden) ?? null);
                        setPreguntaActual(index);
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Group>

            {/* Barra de información y controles */}
            <Group justify="space-between" align="center" wrap="nowrap">
              {/* Navegación */}
              <Group gap="xs" style={{ flex: 1, justifyContent: 'flex-start' }}>
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={preguntaAnterior}
                  disabled={preguntaActual === 0}
                  style={{
                    opacity: preguntaActual === 0 ? 0.3 : 1,
                  }}
                >
                  ←
                </ActionIcon>
                
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={siguientePregunta}
                  disabled={preguntaActual >= examenData.totalPreguntas - 1}
                  style={{
                    opacity: preguntaActual >= examenData.totalPreguntas - 1 ? 0.3 : 1,
                  }}
                >
                  →
                </ActionIcon>
              </Group>

              {/* Info central */}
              <Box style={{ display: 'flex', justifyContent: 'center', minWidth: '80px' }}>
                <Badge color="gray" variant="light" size="lg">
                  {preguntaActual + 1} / {examenData.totalPreguntas}
                </Badge>
              </Box>

              {/* Botones de acción a la derecha.
                  En la última pregunta solo se muestra "Finalizar" (que además
                  guarda la respuesta seleccionada antes de abrir el modal). */}
              <Group gap="sm" style={{ flex: 1, justifyContent: 'flex-end' }}>
                {preguntaActual === examenData.totalPreguntas - 1 ? (
                  <Button
                    variant="filled"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      // Guardar la selección de la última pregunta antes de finalizar.
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual(nuevas);
                      setModalFinalizarAbierto(true);
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    Finalizar
                  </Button>
                ) : (
                  <Button
                    variant="light"
                    color="dark"
                    size="sm"
                    onClick={() => {
                      if (!examenData || !pregunta) return;
                      const nuevas = new Map(respuestas);
                      if (seleccionTemporal) nuevas.set(pregunta.orden, seleccionTemporal);
                      else nuevas.delete(pregunta.orden);
                      setRespuestas(nuevas);
                      guardarProgresoActual(nuevas);
                      const nextIndex = preguntaActual + 1;
                      const nextP = examenData.preguntas[nextIndex];
                      setSeleccionTemporal(nuevas.get(nextP.orden) ?? null);
                      setPreguntaActual(nextIndex);
                    }}
                    disabled={loading}
                    radius="md"
                  >
                    Guardar respuesta
                  </Button>
                )}
              </Group>
            </Group>
          </Box>
        </Paper>
      </Box>
      )}
    </>
  );
};

export default ExamenRapidoPage;
